import { useState } from "react";
import { useTranslation } from "react-i18next";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Card, Input } from "antd";

import { getSubscriptionPlanPrice } from "../../api/subscription";
import { userStore } from "../../stores/userStore";
import { usePendingAction } from "../../utils/usePendingAction";

import "./SubscriptionPromoCode.css";

import type {
  SubscriptionPlan,
  SubscriptionSettingsUpdate,
} from "../../api/subscription";

export interface PromoResult {
  coupon: string;
  finalPrice: { amount: number; currency: string };
  discount: number;
  discountPercent: number | null;
  couponPermanent: boolean | null;
}

interface Props {
  selectedPlan: SubscriptionPlan;
  selectedPeriod: string;
  settings?: SubscriptionSettingsUpdate;
  onPromoApply: (result: PromoResult | null) => void;
}

export const SubscriptionPromoCode = ({
  selectedPlan,
  selectedPeriod,
  settings,
  onPromoApply,
}: Props) => {
  const { t } = useTranslation();

  const [promoVisible, setPromoVisible] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoStatus, setPromoStatus] = useState<string | null>(null);
  const [promoDiscountPercent, setPromoDiscountPercent] = useState<
    number | null
  >(null);
  const { pending: promoLoading, run: runApplyPromo } = usePendingAction();

  const handlePromoInputChange = (value: string) => {
    setPromoInput(value);
    setPromoStatus(null);
    setPromoDiscountPercent(null);
    onPromoApply(null);
  };

  const handleApplyPromo = async () => {
    if (promoStatus === "ok") return;
    if (!promoInput) {
      setPromoStatus("no_coupon");
      return;
    }
    await runApplyPromo(async () => {
      try {
        const result = await getSubscriptionPlanPrice({
          plan: selectedPlan.id,
          coupon: promoInput,
          currency: userStore.currency,
          interval: selectedPeriod,
          settings,
        });
        const curr = userStore.currency.toLowerCase();
        setPromoStatus(result.coupon_status);
        if (result.coupon_status === "ok") {
          const finalPrice = {
            amount: result.price_discounted[selectedPeriod]?.[curr] ?? 0,
            currency: curr.toUpperCase(),
          };
          const orig = result.price_original[selectedPeriod]?.[curr] ?? 0;
          const disc = result.price_discounted[selectedPeriod]?.[curr] ?? 0;
          setPromoDiscountPercent(result.discount_temporal);
          onPromoApply({
            coupon: promoInput,
            finalPrice,
            discount: orig - disc,
            discountPercent: result.discount_temporal,
            couponPermanent: result.coupon_permanent ?? null,
          });
        }
      } catch {
        setPromoStatus("unknown_error");
      }
    });
  };

  const promoErrorMessage = (status: string): string => {
    switch (status) {
      case "no_coupon":
        return t("subscription.pleaseEnterPromoCode", {
          defaultValue: "Please enter your promo code",
        });
      case "err_not_found":
        return t("subscription.promoCodeDoesNotExist", {
          defaultValue: "This promo code does not exist",
        });
      case "err_expired_date":
      case "err_expired_count":
        return t("subscription.promoCodeExpired", {
          defaultValue: "This promo code has expired",
        });
      case "err_max_price":
        return t("subscription.promoCodeItemLimit", {
          defaultValue: "Promo code does not apply to this item",
        });
      case "err_wrong_email":
        return t("subscription.promoCodeWrongEmail", {
          defaultValue: "This promo code is not applicable for this account",
        });
      default:
        return t("subscription.promoCodeError", {
          defaultValue: "Something went wrong",
        });
    }
  };

  return (
    <Card
      styles={{ body: { padding: 16 } }}
      className="subscription-checkout__right-card"
    >
      <div className="subscription-promo">
        <Button
          type="link"
          onClick={() => setPromoVisible(!promoVisible)}
          style={{ paddingLeft: 0 }}
          icon={
            <Icon
              path={promoVisible ? mdiChevronUp : mdiChevronDown}
              size={0.8}
            />
          }
          iconPlacement="end"
        >
          {t("subscription.usePromoCode")}
        </Button>
        {promoVisible && (
          <div className="subscription-promo__input">
            <Input.Search
              value={promoInput}
              onChange={(e) => handlePromoInputChange(e.target.value)}
              onSearch={handleApplyPromo}
              enterButton={
                promoStatus === "ok"
                  ? t("subscription.promoCodeApplied", {
                      defaultValue: "Applied",
                    })
                  : t("subscription.applyPromoCode", {
                      defaultValue: "Apply",
                    })
              }
              loading={promoLoading}
              className={
                promoStatus === "ok"
                  ? "subscription-promo__input--applied"
                  : undefined
              }
              status={
                promoStatus && promoStatus !== "ok" ? "error" : undefined
              }
            />
            {promoStatus === "ok" && (
              <div className="subscription-promo__ok">
                🔥{" "}
                {t("subscription.promoCodeAppliedSuccess", {
                  discount: promoDiscountPercent,
                  defaultValue:
                    "Promo code was applied successfully! Your discount: {{discount}}%",
                })}
              </div>
            )}
            {promoStatus && promoStatus !== "ok" && (
              <div className="subscription-promo__error">
                {promoErrorMessage(promoStatus)}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
